'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import InnerPageHeader from '@/components/InnerPageHeader';
import Toast from '@/components/Toast';
import SuspendedModal from '@/components/SuspendedModal';
import ReturnToAdminButton from '@/components/ReturnToAdminButton';
import { useAuthStatus } from '@/hooks/useAuthStatus';

export default function GymDashboard() {
  const router = useRouter();
  
  // Check auth status automatically (logout if suspended/deleted)
  const { showSuspendedModal, suspendMessage } = useAuthStatus();
  
  const [user, setUser] = useState<any>(null);
  const [gymProfile, setGymProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  
  // Edit states
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isEditingHours, setIsEditingHours] = useState(false);
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [editedProfile, setEditedProfile] = useState({
    firstName: '',
    businessName: '',
    phone: '',
    location: '',
    aboutGym: '',
    services: [] as string[],
    membershipPricing: '',
    membershipDescription: '',
    facilityFeatures: [] as string[],
    operatingHours: {} as any,
    photos: [] as string[],
  });

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (!userData) {
      router.push('/login');
      return;
    }

    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    fetchGymProfile(parsedUser.email);
  }, [router]);

  const fetchGymProfile = async (email: string) => {
    try {
      const response = await fetch(`/api/gym/profile?email=${email}`);
      const data = await response.json();

      if (response.ok && data.gym) {
        setGymProfile(data.gym);
        setEditedProfile({
          firstName: data.gym.firstName,
          businessName: data.gym.businessName || '',
          phone: data.gym.phone,
          location: data.gym.location,
          aboutGym: data.gym.aboutGym,
          services: data.gym.services,
          membershipPricing: data.gym.membershipPricing || '',
          membershipDescription: data.gym.membershipDescription,
          facilityFeatures: data.gym.facilityFeatures,
          operatingHours: data.gym.operatingHours,
          photos: data.gym.photos || [],
        });
      }
    } catch (error) {
      console.error('Error fetching gym profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    router.push('/login');
  };

  const handleSaveProfile = async () => {
    try {
      const response = await fetch('/api/gym/update', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: user.email,
          ...editedProfile,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setToast({ message: 'Gym profile updated successfully!', type: 'success' });
        setGymProfile(data.gym);
        setEditedProfile({
          firstName: data.gym.firstName,
          businessName: data.gym.businessName || '',
          phone: data.gym.phone,
          location: data.gym.location,
          aboutGym: data.gym.aboutGym,
          services: data.gym.services,
          membershipPricing: data.gym.membershipPricing || '',
          membershipDescription: data.gym.membershipDescription,
          facilityFeatures: data.gym.facilityFeatures,
          operatingHours: data.gym.operatingHours,
          photos: data.gym.photos || [],
        });
        setIsEditingProfile(false);
      } else {
        setToast({ message: data.error || 'Failed to update profile', type: 'error' });
      }
    } catch (error) {
      setToast({ message: 'Failed to update profile', type: 'error' });
    }
  };

  const handleSaveHours = async () => {
    try {
      const response = await fetch('/api/gym/update', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: user.email,
          operatingHours: editedProfile.operatingHours,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setToast({ message: 'Operating hours updated successfully!', type: 'success' });
        setGymProfile(data.gym);
        setIsEditingHours(false);
      } else {
        setToast({ message: data.error || 'Failed to update hours', type: 'error' });
      }
    } catch (error) {
      setToast({ message: 'Failed to update hours', type: 'error' });
    }
  };

  const handleGymPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    const newPhotos: string[] = [];

    Array.from(files).forEach((file) => {
      if (!validTypes.includes(file.type)) {
        setToast({ message: 'Please upload PNG, JPG, JPEG, or WEBP images', type: 'error' });
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setToast({ message: 'Each image must be less than 5MB', type: 'error' });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          const MAX_SIZE = 1200;
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > MAX_SIZE) {
              height *= MAX_SIZE / width;
              width = MAX_SIZE;
            }
          } else {
            if (height > MAX_SIZE) {
              width *= MAX_SIZE / height;
              height = MAX_SIZE;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);
          
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);
          newPhotos.push(compressedBase64);
          
          if (newPhotos.length === files.length) {
            setEditedProfile(prev => ({
              ...prev,
              photos: [...(prev.photos || []), ...newPhotos]
            }));
            setToast({ message: 'Photos uploaded! Click Save to apply changes.', type: 'success' });
          }
        };
        img.src = reader.result as string;
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index: number) => {
    setEditedProfile(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
    setToast({ message: 'Photo removed. Click Save to apply changes.', type: 'success' });
  };

  const handlePasswordChange = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setToast({ message: 'Please fill in all password fields', type: 'error' });
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setToast({ message: 'New password must be at least 8 characters', type: 'error' });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setToast({ message: 'New passwords do not match', type: 'error' });
      return;
    }

    try {
      const response = await fetch('/api/user/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: user.email,
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setToast({ message: 'Password changed successfully!', type: 'success' });
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setIsEditingPassword(false);
      } else {
        setToast({ message: data.error || 'Failed to change password', type: 'error' });
      }
    } catch (error) {
      setToast({ message: 'Failed to change password', type: 'error' });
    }
  };

  const toggleDayOpen = (day: string) => {
    setEditedProfile(prev => ({
      ...prev,
      operatingHours: {
        ...prev.operatingHours,
        [day]: {
          ...prev.operatingHours[day],
          isOpen: !prev.operatingHours[day]?.isOpen
        }
      }
    }));
  };

  const updateDayHours = (day: string, field: 'openTime' | 'closeTime', value: string) => {
    setEditedProfile(prev => ({
      ...prev,
      operatingHours: {
        ...prev.operatingHours,
        [day]: {
          ...prev.operatingHours[day],
          [field]: value
        }
      }
    }));
  };

  const serviceOptions = [
    'Personal Training', 'Group Classes', 'Yoga', 'Pilates', 'Cardio Equipment',
    'Weight Training', 'Swimming Pool', 'Sauna', 'Spa', 'Nutrition Counseling'
  ];

  const facilityOptions = [
    'Locker Rooms', 'Showers', 'Parking', 'WiFi', 'Childcare',
    'Cafe', 'Pro Shop', 'Towel Service', 'Air Conditioning', 'Music System'
  ];

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F6EFED] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#8a7296] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="font-['Archivo',sans-serif] text-[16px] text-[#6e6672]">Loading...</p>
        </div>
      </div>
    );
  }

  if (!gymProfile) {
    return (
      <div className="min-h-screen bg-[#F6EFED]">
        <InnerPageHeader />
        <div className="max-w-[1200px] mx-auto px-[20px] py-20">
          <div className="bg-white rounded-[24px] p-12 text-center">
            <h2 className="font-['Gotham_Rounded',sans-serif] text-[32px] font-bold text-[#2E2A3A] mb-4">
              No Gym Profile Found
            </h2>
            <p className="font-['Archivo',sans-serif] text-[16px] text-[#6e6672] mb-8">
              You haven't registered a gym yet.
            </p>
            <button
              onClick={() => router.push('/signup/gym')}
              className="px-8 py-3 bg-[#8a7296] text-white font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#7a647d] transition-colors"
            >
              Register Your Gym
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6EFED]">
      <ReturnToAdminButton />
      <InnerPageHeader />

      <section className="py-12">
        <div className="max-w-[1380px] mx-auto px-[20px]">
          <div className="grid md:grid-cols-[280px_1fr] gap-8">
            
            {/* Sidebar */}
            <aside className="bg-white rounded-[24px] p-6 h-fit shadow-sm">
              {/* Gym Logo/Photo */}
              <div className="text-center mb-6">
                <div className="w-24 h-24 mx-auto mb-4 relative group cursor-pointer">
                  <img
                    src={editedProfile.photos && editedProfile.photos.length > 0 ? editedProfile.photos[0] : (gymProfile.photos && gymProfile.photos.length > 0 ? gymProfile.photos[0] : '')}
                    alt={gymProfile.businessName || 'Gym'}
                    className="w-full h-full rounded-full object-cover border-4 border-[#8a7296]"
                  />
                  {isEditingProfile && (
                    <>
                      <label htmlFor="gym-photo-upload" className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </label>
                      <input
                        id="gym-photo-upload"
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        multiple
                        onChange={handleGymPhotoChange}
                        className="hidden"
                      />
                    </>
                  )}
                </div>
                <h3 className="font-['Gotham_Rounded',sans-serif] text-[18px] font-bold text-[#2E2A3A]">
                  {gymProfile.businessName || `${gymProfile.firstName}'s Gym`}
                </h3>
                <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">
                  {user?.email}
                </p>
                
                {/* Status Badge */}
                <div className="mt-3">
                  <span className={`inline-block px-3 py-1 rounded-full font-['Archivo',sans-serif] text-[12px] font-medium ${
                    gymProfile.status === 'approved' 
                      ? 'bg-green-100 text-green-700' 
                      : gymProfile.status === 'pending'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {gymProfile.status === 'approved' && '✓ Approved'}
                    {gymProfile.status === 'pending' && '⏳ Pending'}
                    {gymProfile.status === 'rejected' && '✗ Rejected'}
                  </span>
                </div>
              </div>

              {/* Navigation */}
              <nav className="space-y-2">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`w-full text-left px-4 py-3 rounded-lg font-['Archivo',sans-serif] text-[16px] transition-colors ${
                    activeTab === 'overview'
                      ? 'bg-[#8A7296] text-white'
                      : 'text-[#2E2A3A] hover:bg-[#F6EFED]'
                  }`}
                >
                  Overview
                </button>
                <button
                  onClick={() => setActiveTab('profile')}
                  className={`w-full text-left px-4 py-3 rounded-lg font-['Archivo',sans-serif] text-[16px] transition-colors ${
                    activeTab === 'profile'
                      ? 'bg-[#8A7296] text-white'
                      : 'text-[#2E2A3A] hover:bg-[#F6EFED]'
                  }`}
                >
                  Gym Profile
                </button>
                <button
                  onClick={() => setActiveTab('hours')}
                  className={`w-full text-left px-4 py-3 rounded-lg font-['Archivo',sans-serif] text-[16px] transition-colors ${
                    activeTab === 'hours'
                      ? 'bg-[#8A7296] text-white'
                      : 'text-[#2E2A3A] hover:bg-[#F6EFED]'
                  }`}
                >
                  Operating Hours
                </button>
                <button
                  onClick={() => setActiveTab('settings')}
                  className={`w-full text-left px-4 py-3 rounded-lg font-['Archivo',sans-serif] text-[16px] transition-colors ${
                    activeTab === 'settings'
                      ? 'bg-[#8A7296] text-white'
                      : 'text-[#2E2A3A] hover:bg-[#F6EFED]'
                  }`}
                >
                  Settings
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-3 rounded-lg font-['Archivo',sans-serif] text-[16px] text-red-600 hover:bg-red-50 transition-colors"
                >
                  Logout
                </button>
              </nav>
            </aside>

            {/* Main Content */}
            <main>
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div>
                  <h1 className="font-['Gotham_Rounded',sans-serif] text-[36px] font-bold text-[#2E2A3A] mb-8">
                    Welcome back, {gymProfile.firstName}! 👋
                  </h1>

                  {/* Stats Cards */}
                  <div className="grid md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white rounded-[20px] p-6 shadow-sm">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-[#8A7296]/10 rounded-full flex items-center justify-center">
                          <svg className="w-6 h-6 text-[#8A7296]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">Services</p>
                          <p className="font-['Gotham_Rounded',sans-serif] text-[24px] font-bold text-[#2E2A3A]">
                            {gymProfile.services.length}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-[20px] p-6 shadow-sm">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-[#8A7296]/10 rounded-full flex items-center justify-center">
                          <svg className="w-6 h-6 text-[#8A7296]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">Photos</p>
                          <p className="font-['Gotham_Rounded',sans-serif] text-[24px] font-bold text-[#2E2A3A]">
                            {gymProfile.photos.length}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-[20px] p-6 shadow-sm">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-[#8A7296]/10 rounded-full flex items-center justify-center">
                          <svg className="w-6 h-6 text-[#8A7296]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">Facilities</p>
                          <p className="font-['Gotham_Rounded',sans-serif] text-[24px] font-bold text-[#2E2A3A]">
                            {gymProfile.facilityFeatures.length}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Quick Info */}
                  <div className="bg-white rounded-[24px] p-8 shadow-sm mb-8">
                    <h2 className="font-['Gotham_Rounded',sans-serif] text-[24px] font-bold text-[#2E2A3A] mb-6">
                      Quick Info
                    </h2>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672] mb-1">Location</p>
                        <p className="font-['Archivo',sans-serif] text-[16px] text-[#2E2A3A] font-medium">
                          📍 {gymProfile.location}
                        </p>
                      </div>
                      <div>
                        <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672] mb-1">Phone</p>
                        <p className="font-['Archivo',sans-serif] text-[16px] text-[#2E2A3A] font-medium">
                          📱 {gymProfile.phone}
                        </p>
                      </div>
                      {gymProfile.membershipPricing && (
                        <div>
                          <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672] mb-1">Membership Pricing</p>
                          <p className="font-['Archivo',sans-serif] text-[16px] text-[#2E2A3A] font-medium">
                            💰 {gymProfile.membershipPricing}
                          </p>
                        </div>
                      )}
                      <div>
                        <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672] mb-1">Contact Person</p>
                        <p className="font-['Archivo',sans-serif] text-[16px] text-[#2E2A3A] font-medium">
                          👤 {gymProfile.firstName}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Gym Photos Gallery */}
                  {gymProfile.photos && gymProfile.photos.length > 0 && (
                    <div className="bg-white rounded-[24px] p-8 shadow-sm">
                      <h2 className="font-['Gotham_Rounded',sans-serif] text-[24px] font-bold text-[#2E2A3A] mb-6">
                        Gym Photos
                      </h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {gymProfile.photos.slice(0, 6).map((photo: string, index: number) => (
                          <div key={index} className="relative group overflow-hidden rounded-[16px]">
                            <img
                              src={photo}
                              alt={`Gym Photo ${index + 1}`}
                              className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          </div>
                        ))}
                      </div>
                      {gymProfile.photos.length > 6 && (
                        <div className="mt-4 text-center">
                          <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">
                            +{gymProfile.photos.length - 6} more photos
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Gym Profile Tab */}
              {activeTab === 'profile' && (
                <div>
                  <div className="flex justify-between items-center mb-8">
                    <h1 className="font-['Gotham_Rounded',sans-serif] text-[36px] font-bold text-[#2E2A3A]">
                      Gym Profile
                    </h1>
                    {!isEditingProfile && (
                      <button
                        onClick={() => setIsEditingProfile(true)}
                        className="px-6 py-3 bg-[#8A7296] text-white font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#7a647d] transition-colors"
                      >
                        Edit Profile
                      </button>
                    )}
                  </div>

                  <div className="bg-white rounded-[24px] p-8 shadow-sm space-y-6">
                    {/* Basic Info */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                          Contact Person
                        </label>
                        <input
                          type="text"
                          value={isEditingProfile ? editedProfile.firstName : gymProfile.firstName}
                          onChange={(e) => setEditedProfile({ ...editedProfile, firstName: e.target.value })}
                          readOnly={!isEditingProfile}
                          className={`w-full px-4 py-3 border border-[#d7d7d7] rounded-[10px] font-['Archivo',sans-serif] text-[16px] ${
                            isEditingProfile ? 'bg-white' : 'bg-[#F6EFED]/50'
                          }`}
                        />
                      </div>
                      <div>
                        <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                          Gym Name
                        </label>
                        <input
                          type="text"
                          value={isEditingProfile ? editedProfile.businessName : gymProfile.businessName || ''}
                          onChange={(e) => setEditedProfile({ ...editedProfile, businessName: e.target.value })}
                          readOnly={!isEditingProfile}
                          placeholder="e.g. FitZone Women's Gym"
                          className={`w-full px-4 py-3 border border-[#d7d7d7] rounded-[10px] font-['Archivo',sans-serif] text-[16px] ${
                            isEditingProfile ? 'bg-white' : 'bg-[#F6EFED]/50'
                          }`}
                        />
                      </div>
                      <div>
                        <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                          Phone
                        </label>
                        <input
                          type="tel"
                          value={isEditingProfile ? editedProfile.phone : gymProfile.phone}
                          onChange={(e) => setEditedProfile({ ...editedProfile, phone: e.target.value })}
                          readOnly={!isEditingProfile}
                          className={`w-full px-4 py-3 border border-[#d7d7d7] rounded-[10px] font-['Archivo',sans-serif] text-[16px] ${
                            isEditingProfile ? 'bg-white' : 'bg-[#F6EFED]/50'
                          }`}
                        />
                      </div>
                      <div>
                        <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                          Location
                        </label>
                        <input
                          type="text"
                          value={isEditingProfile ? editedProfile.location : gymProfile.location}
                          onChange={(e) => setEditedProfile({ ...editedProfile, location: e.target.value })}
                          readOnly={!isEditingProfile}
                          className={`w-full px-4 py-3 border border-[#d7d7d7] rounded-[10px] font-['Archivo',sans-serif] text-[16px] ${
                            isEditingProfile ? 'bg-white' : 'bg-[#F6EFED]/50'
                          }`}
                        />
                      </div>
                    </div>

                    {/* About Gym */}
                    <div>
                      <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                        About Gym
                      </label>
                      <textarea
                        value={isEditingProfile ? editedProfile.aboutGym : gymProfile.aboutGym}
                        onChange={(e) => setEditedProfile({ ...editedProfile, aboutGym: e.target.value })}
                        readOnly={!isEditingProfile}
                        rows={6}
                        className={`w-full px-4 py-3 border border-[#d7d7d7] rounded-[10px] font-['Archivo',sans-serif] text-[16px] resize-none ${
                          isEditingProfile ? 'bg-white' : 'bg-[#F6EFED]/50'
                        }`}
                      />
                    </div>

                    {/* Gym Photos Gallery */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A]">
                          Gym Photos
                        </label>
                        {isEditingProfile && (
                          <label htmlFor="gym-photos-upload" className="px-3 py-1 bg-[#8A7296] text-white font-['Archivo',sans-serif] text-[12px] font-medium rounded-[6px] hover:bg-[#7a647d] transition-colors cursor-pointer">
                            Add Photos
                          </label>
                        )}
                        <input
                          id="gym-photos-upload"
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/webp"
                          multiple
                          onChange={handleGymPhotoChange}
                          className="hidden"
                        />
                      </div>
                      
                      {editedProfile.photos && editedProfile.photos.length > 0 ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {editedProfile.photos.map((photo: string, index: number) => (
                            <div key={index} className="relative group">
                              <img
                                src={photo}
                                alt={`Gym Photo ${index + 1}`}
                                className="w-full h-32 object-cover rounded-[12px] border border-[#d7d7d7]"
                              />
                              {isEditingProfile && (
                                <button
                                  type="button"
                                  onClick={() => removePhoto(index)}
                                  className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="border-2 border-dashed border-[#d7d7d7] rounded-[12px] p-8 text-center">
                          <svg className="w-12 h-12 text-[#6e6672] mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">
                            {isEditingProfile ? 'Click "Add Photos" to upload gym images' : 'No photos uploaded yet'}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Services */}
                    <div>
                      <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-3">
                        Services Offered
                      </label>
                      {isEditingProfile ? (
                        <div className="flex flex-wrap gap-2">
                          {serviceOptions.map((service) => (
                            <button
                              key={service}
                              type="button"
                              onClick={() => {
                                const newServices = editedProfile.services.includes(service)
                                  ? editedProfile.services.filter(s => s !== service)
                                  : [...editedProfile.services, service];
                                setEditedProfile({ ...editedProfile, services: newServices });
                              }}
                              className={`px-4 py-2 rounded-full font-['Archivo',sans-serif] text-[14px] font-medium transition-all ${
                                editedProfile.services.includes(service)
                                  ? 'bg-[#8a7296] text-white'
                                  : 'bg-[#f5f2f0] text-[#2E2A3A] hover:bg-[#e5e2e0]'
                              }`}
                            >
                              {service}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {gymProfile.services.map((service: string) => (
                            <span
                              key={service}
                              className="px-4 py-2 bg-[#8a7296] text-white rounded-full font-['Archivo',sans-serif] text-[14px] font-medium"
                            >
                              {service}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Facility Features */}
                    <div>
                      <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-3">
                        Facility Features
                      </label>
                      {isEditingProfile ? (
                        <div className="flex flex-wrap gap-2">
                          {facilityOptions.map((feature) => (
                            <button
                              key={feature}
                              type="button"
                              onClick={() => {
                                const newFeatures = editedProfile.facilityFeatures.includes(feature)
                                  ? editedProfile.facilityFeatures.filter(f => f !== feature)
                                  : [...editedProfile.facilityFeatures, feature];
                                setEditedProfile({ ...editedProfile, facilityFeatures: newFeatures });
                              }}
                              className={`px-4 py-2 rounded-full font-['Archivo',sans-serif] text-[14px] font-medium transition-all ${
                                editedProfile.facilityFeatures.includes(feature)
                                  ? 'bg-[#8a7296] text-white'
                                  : 'bg-[#f5f2f0] text-[#2E2A3A] hover:bg-[#e5e2e0]'
                              }`}
                            >
                              {feature}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {gymProfile.facilityFeatures.map((feature: string) => (
                            <span
                              key={feature}
                              className="px-4 py-2 bg-[#f5f2f0] text-[#2E2A3A] rounded-full font-['Archivo',sans-serif] text-[14px] font-medium"
                            >
                              {feature}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Edit Actions */}
                    {isEditingProfile && (
                      <div className="flex gap-4 pt-4">
                        <button
                          onClick={handleSaveProfile}
                          className="px-8 py-3 bg-[#8A7296] text-white font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#7a647d] transition-colors"
                        >
                          Save Changes
                        </button>
                        <button
                          onClick={() => {
                            setIsEditingProfile(false);
                            fetchGymProfile(user.email);
                          }}
                          className="px-8 py-3 border border-[#d7d7d7] text-[#2E2A3A] font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#F6EFED] transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Operating Hours Tab */}
              {activeTab === 'hours' && (
                <div>
                  <div className="flex justify-between items-center mb-8">
                    <h1 className="font-['Gotham_Rounded',sans-serif] text-[36px] font-bold text-[#2E2A3A]">
                      Operating Hours
                    </h1>
                    {!isEditingHours ? (
                      <button
                        onClick={() => setIsEditingHours(true)}
                        className="px-6 py-3 bg-[#8A7296] text-white font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#7a647d] transition-colors"
                      >
                        Edit Hours
                      </button>
                    ) : (
                      <div className="flex gap-3">
                        <button
                          onClick={handleSaveHours}
                          className="px-6 py-3 bg-[#8A7296] text-white font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#7a647d] transition-colors"
                        >
                          Save Changes
                        </button>
                        <button
                          onClick={() => {
                            setIsEditingHours(false);
                            fetchGymProfile(user.email);
                          }}
                          className="px-6 py-3 border border-[#d7d7d7] text-[#2E2A3A] font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#F6EFED] transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="bg-white rounded-[24px] p-8 shadow-sm">
                    <div className="space-y-4">
                      {daysOfWeek.map((day) => {
                        const dayData = editedProfile.operatingHours[day] || { isOpen: false, openTime: '09:00', closeTime: '17:00' };
                        return (
                          <div key={day} className="p-6 bg-[#f5f2f0] rounded-[16px]">
                            <div className="flex items-center justify-between mb-4">
                              <p className="font-['Gotham_Rounded',sans-serif] text-[18px] font-bold text-[#2E2A3A]">
                                {day}
                              </p>
                              {isEditingHours && (
                                <button
                                  onClick={() => toggleDayOpen(day)}
                                  className={`px-4 py-2 rounded-lg font-['Archivo',sans-serif] text-[14px] font-medium transition-colors ${
                                    dayData.isOpen
                                      ? 'bg-[#8a7296] text-white'
                                      : 'bg-white text-[#2E2A3A] border border-[#d7d7d7]'
                                  }`}
                                >
                                  {dayData.isOpen ? 'Open' : 'Closed'}
                                </button>
                              )}
                            </div>
                            
                            {dayData.isOpen && (
                              <div className="flex items-center gap-4">
                                {isEditingHours ? (
                                  <>
                                    <div className="flex-1">
                                      <label className="block font-['Archivo',sans-serif] text-[12px] text-[#6e6672] mb-1">
                                        Open Time
                                      </label>
                                      <input
                                        type="time"
                                        value={dayData.openTime}
                                        onChange={(e) => updateDayHours(day, 'openTime', e.target.value)}
                                        className="w-full px-3 py-2 border border-[#d7d7d7] rounded-lg font-['Archivo',sans-serif] text-[14px]"
                                      />
                                    </div>
                                    <span className="text-[#6e6672] mt-5">-</span>
                                    <div className="flex-1">
                                      <label className="block font-['Archivo',sans-serif] text-[12px] text-[#6e6672] mb-1">
                                        Close Time
                                      </label>
                                      <input
                                        type="time"
                                        value={dayData.closeTime}
                                        onChange={(e) => updateDayHours(day, 'closeTime', e.target.value)}
                                        className="w-full px-3 py-2 border border-[#d7d7d7] rounded-lg font-['Archivo',sans-serif] text-[14px]"
                                      />
                                    </div>
                                  </>
                                ) : (
                                  <p className="font-['Archivo',sans-serif] text-[16px] text-[#2E2A3A]">
                                    🕐 {dayData.openTime} - {dayData.closeTime}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Settings Tab */}
              {activeTab === 'settings' && (
                <div>
                  <h1 className="font-['Gotham_Rounded',sans-serif] text-[36px] font-bold text-[#2E2A3A] mb-8">
                    Settings
                  </h1>

                  {/* Change Password */}
                  <div className="bg-white rounded-[24px] p-8 shadow-sm mb-6">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="font-['Gotham_Rounded',sans-serif] text-[20px] font-bold text-[#2E2A3A]">
                        Change Password
                      </h2>
                      {!isEditingPassword && (
                        <button
                          onClick={() => setIsEditingPassword(true)}
                          className="px-4 py-2 bg-[#8A7296] text-white font-['Archivo',sans-serif] text-[14px] font-medium rounded-[8px] hover:bg-[#7a647d] transition-colors"
                        >
                          Change Password
                        </button>
                      )}
                    </div>

                    {isEditingPassword && (
                      <div className="space-y-4">
                        <div>
                          <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                            Current Password
                          </label>
                          <input
                            type="password"
                            value={passwordData.currentPassword}
                            onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                            className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[10px] font-['Archivo',sans-serif] text-[16px]"
                          />
                        </div>
                        <div>
                          <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                            New Password
                          </label>
                          <input
                            type="password"
                            value={passwordData.newPassword}
                            onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                            className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[10px] font-['Archivo',sans-serif] text-[16px]"
                          />
                        </div>
                        <div>
                          <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                            Confirm New Password
                          </label>
                          <input
                            type="password"
                            value={passwordData.confirmPassword}
                            onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                            className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[10px] font-['Archivo',sans-serif] text-[16px]"
                          />
                        </div>
                        <div className="flex gap-4 pt-2">
                          <button
                            onClick={handlePasswordChange}
                            className="px-6 py-3 bg-[#8A7296] text-white font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#7a647d] transition-colors"
                          >
                            Update Password
                          </button>
                          <button
                            onClick={() => {
                              setIsEditingPassword(false);
                              setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                            }}
                            className="px-6 py-3 border border-[#d7d7d7] text-[#2E2A3A] font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#F6EFED] transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </main>
          </div>
        </div>
      </section>

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Suspended Modal */}
      {showSuspendedModal && (
        <SuspendedModal message={suspendMessage} />
      )}
    </div>
  );
}
